import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Users, Star, Ticket, ChevronRight } from 'lucide-react';
import gsap from 'gsap';
import { useTheme } from 'next-themes';
import { useLanguage } from '@/contexts/LanguageContext';

import heroBgDark from '@/assets/hero-main-theme.webp';
import heroBgLight from '@/assets/hero-day-theme.webp';
import travelerImg from '@/assets/travler.webp';

export default function AnimatedHero() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const { t } = useLanguage();

  const sectionRef = useRef<HTMLElement>(null);
  const bgRef = useRef<HTMLImageElement>(null);
  const travelerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const descRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const trustRef = useRef<HTMLDivElement>(null);
  const ambientRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);

  // Prevent hydration mismatch
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // ── Cinematic entry timeline ──
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      // Background: Ken Burns zoom-in reveal (ends at 1.1 to match scroll parallax scale)
      tl.fromTo(bgRef.current,
        { scale: 1.3, opacity: 0 },
        { scale: 1.1, opacity: 1, duration: 2 }
      );

      // Overlay fades in
      tl.fromTo(overlayRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 1.2 },
        '-=1.4'
      );

      // Traveler slides up from below
      tl.fromTo(travelerRef.current,
        { y: 80, opacity: 0, scale: 0.95 },
        { y: 0, opacity: 1, scale: 1, duration: 1.2, ease: 'power2.out' },
        '-=0.8'
      );

      // Ambient effects fade in (only in dark mode)
      if (ambientRef.current) {
        tl.fromTo(ambientRef.current,
          { opacity: 0 },
          { opacity: 1, duration: 1 },
          '-=0.6'
        );
      }

      // Content stagger: badge → heading → description → CTA → trust badges
      tl.fromTo(badgeRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6 },
        '-=0.8'
      )
      .fromTo(headingRef.current,
        { y: 40, opacity: 0, clipPath: 'inset(0 0 100% 0)' },
        { y: 0, opacity: 1, clipPath: 'inset(0 0 0% 0)', duration: 0.8, ease: 'power4.out' },
        '-=0.3'
      )
      .fromTo(descRef.current,
        { y: 25, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6 },
        '-=0.3'
      )
      .fromTo(ctaRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5 },
        '-=0.2'
      )
      .fromTo(trustRef.current,
        { y: 15, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.4 },
        '-=0.2'
      );

      // ── Scroll parallax (starts after entry completes) ──
      // Respect reduced-motion user preference
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const handleScroll = () => {
        if (prefersReducedMotion) return;

        const scrollY = window.scrollY;
        const section = sectionRef.current;
        if (!section) return;

        // Normalize scroll progress: 0 at top → 1 when section is fully scrolled past
        const sectionH = section.offsetHeight;
        const progress = Math.min(Math.max(scrollY / sectionH, 0), 1);

        // Background: slower parallax (moves down 30% of scroll)
        if (bgRef.current) {
          bgRef.current.style.transform = `translate3d(0, ${scrollY * 0.3}px, 0) scale(1.1)`;
        }

        // Character: stays grounded, fades out and scales down in place
        if (travelerRef.current) {
          const charOpacity = 1 - progress * 1.3; // fades out faster (gone by ~77% scroll)
          const charScale = 1 - 0.06 * progress;  // 1 → 0.94
          travelerRef.current.style.transform = `translate3d(0, 0, 0) scale(${charScale})`;
          travelerRef.current.style.opacity = String(Math.max(charOpacity, 0));
        }

        // Content: subtle upward drift, fades out slightly later
        if (contentRef.current) {
          const contentY = -40 * progress;
          const contentOpacity = 1 - progress * 0.85;
          contentRef.current.style.transform = `translate3d(0, ${contentY}px, 0)`;
          contentRef.current.style.opacity = String(Math.max(contentOpacity, 0));
        }
      };

      // Only attach scroll parallax after entry animation finishes
      tl.call(() => {
        if (!prefersReducedMotion) {
          window.addEventListener('scroll', handleScroll, { passive: true });
        }
      });

      // Store reference for cleanup
      (sectionRef.current as any).__scrollHandler = handleScroll;

    }, sectionRef);

    return () => {
      ctx.revert();
      const handler = (sectionRef.current as any)?.__scrollHandler;
      if (handler) window.removeEventListener('scroll', handler);
    };
  }, []);

  // Choose hero background based on theme
  const heroBg = mounted ? (isDark ? heroBgDark : heroBgLight) : heroBgDark;

  // Theme-aware text colors
  const textColor = isDark ? 'text-[hsl(40,10%,92%)]' : 'text-[hsl(220,28%,12%)]';

  return (
    <section ref={sectionRef} className="relative min-h-[100svh] flex items-end sm:items-center overflow-hidden">
      {/* Background — swaps with theme */}
      <img
        ref={bgRef}
        src={heroBg}
        width={1920}
        height={1080}
        fetchPriority="high"
        alt={isDark ? 'Star Line terminal at night' : 'Star Line terminal in daylight'}
        className="absolute inset-0 w-full h-full object-cover object-center md:object-[center_30%] will-change-transform transition-none opacity-0"
        draggable={false}
      />

      {/* Traveler overlay — parallax character layer */}
      <div
        ref={travelerRef}
        className="absolute bottom-0 right-[2%] md:right-[6%] z-[5] hidden md:block opacity-0 will-change-[transform,opacity]"
        style={{ transformOrigin: 'center bottom' }}
      >
        <img
          src={travelerImg}
          alt="Traveler checking ticket"
          width={400}
          height={720}
          className={`h-[60vh] lg:h-[72vh] w-auto object-contain ${isDark ? 'drop-shadow-[0_8px_40px_rgba(0,0,0,0.6)] brightness-[82%] contrast-[112%] saturate-[85%]' : 'drop-shadow-[0_8px_30px_rgba(0,0,0,0.3)] brightness-[110%] contrast-[115%] saturate-[115%]'}`}
          draggable={false}
        />
        {/* Ground contact shadow */}
        <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-[70%] h-5 rounded-[50%] blur-lg ${isDark ? 'bg-black/30' : 'bg-black/15'}`} />
      </div>

      {/* Ambient Effects — only in dark mode */}
      {isDark && (
        <div ref={ambientRef} className="absolute inset-0 pointer-events-none z-[6] opacity-0">
          <div className="lamppost-glow" />
          <div className="fog-drift" />
          <div className="headlight-bloom" />
          <div className="road-shimmer" />
        </div>
      )}

      {/* Gradient overlays — theme-aware */}
      <div ref={overlayRef} className="absolute inset-0 z-[7] opacity-0">
        {isDark ? (
          <>
            {/* Left content scrim — just enough to keep text readable */}
            <div className="absolute inset-0 bg-gradient-to-r from-[hsl(220,28%,6%,0.78)] via-[hsl(220,28%,6%,0.45)] to-transparent" />
            {/* Bottom anchor — subtle fade to page background */}
            <div className="absolute inset-0 bg-gradient-to-t from-[hsl(220,28%,6%,0.7)] via-transparent to-[hsl(220,28%,6%,0.15)]" />
            {/* Mobile: light scrim so image is still visible */}
            <div className="absolute inset-0 md:hidden bg-[hsl(220,28%,6%,0.35)]" />
          </>
        ) : (
          <>
            {/* Left content scrim — light and airy, no milky washout */}
            <div className="absolute inset-0 bg-gradient-to-r from-[hsl(0,0%,100%,0.6)] via-[hsl(0,0%,100%,0.2)] to-transparent" />
            {/* Bottom anchor */}
            <div className="absolute inset-0 bg-gradient-to-t from-[hsl(0,0%,100%,0.35)] via-transparent to-transparent" />
            {/* Mobile: minimal scrim */}
            <div className="absolute inset-0 md:hidden bg-[hsl(0,0%,100%,0.2)]" />
          </>
        )}
        <div className="absolute bottom-0 left-0 right-0 h-24 sm:h-32 bg-gradient-to-t from-background to-transparent" />
      </div>

      {/* Content */}
      <div ref={contentRef} className={`container relative pt-24 pb-10 sm:pt-36 sm:pb-12 md:pt-40 md:pb-16 lg:pt-44 lg:pb-24 z-[8] will-change-[transform,opacity] ${textColor}`}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            <div ref={badgeRef} className="inline-flex items-center gap-2 bg-primary/8 border border-primary/15 rounded-full px-3 sm:px-4 py-1 sm:py-1.5 mb-5 sm:mb-8 opacity-0">
              <Star className="w-3.5 h-3.5 text-accent-strong" />
              <span className="text-xs font-semibold tracking-wide text-accent-strong uppercase">{t('home_eyebrow')}</span>
            </div>

            <h1 ref={headingRef} className="font-display text-fluid-h1 font-bold mb-5 sm:mb-6 tracking-tight opacity-0">
              {t('home_hero_line1')}<br/>
              <span className="text-gradient-primary">{t('home_hero_line2')}</span>
            </h1>

            <p ref={descRef} className={`text-sm sm:text-base md:text-lg max-w-md leading-relaxed mb-6 sm:mb-10 opacity-0 ${isDark ? 'text-muted-foreground' : 'text-[hsl(220,15%,40%)]'}`}>
              {t('home_hero_sub')}
            </p>

            <div ref={ctaRef} className="flex flex-col sm:flex-row gap-3 mb-6 opacity-0">
              <Link
                to="/search"
                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 rounded-xl font-semibold text-sm hover:bg-primary/90 transition-all btn-primary-glow"
              >
                <Ticket className="w-4 h-4" />
                {t('nav_search')}
              </Link>
              <Link
                to="/routes"
                className="inline-flex items-center justify-center gap-2 bg-secondary border border-border text-foreground px-7 py-3.5 rounded-xl font-semibold text-sm hover:bg-secondary/80 transition-all"
              >
                {t('home_explore_routes')}
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            <div ref={trustRef} className="flex items-center gap-6 text-xs text-muted-foreground opacity-0">
              <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-success" /> {t('home_verified_operator')}</span>
              {/* "2M+ Passengers" was invented — there is no passenger total
                  anywhere in the system to back it. AGENTS.md forbids fabricated
                  metrics, so this states what is actually verifiable. */}
              <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-accent-strong" /> {t('home_live_seats')}</span>
            </div>
          </div>

          <div className="hidden lg:block" />
        </div>
      </div>
    </section>
  );
}
